import React, { useCallback, useEffect } from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';
import { Paper, PaperProps } from '@mui/material';
import { useTypedSelector } from '@Features/store';
import { useDispatch } from 'react-redux';
import { getClassroomUnitsAnalytics } from '@Features/classrooms/classroomSlice';
import { GridLayout } from '@Styled/utilities';
import { formatDuration } from '@Utils/formatDuration';
import { unitTypeColors } from '@Constants/unitTypeColors';

const pluralToSingular: Record<string, string> = {
	videos: 'video',
	documents: 'document',
	tests: 'test',
};

const ClassroomUnitsAnalytics = () => {
	const dispatch = useDispatch();

	const {
		currentClassroom,
		analytics: {
			all: { singleClassroomUnitsAnalytics },
		},
	} = useTypedSelector((state) => state.classroom);

	const pickTypeColor = useCallback((type: string) => {
		const color = unitTypeColors[pluralToSingular[type] ?? type];
		return color ? rgba(color, 0.1) : undefined;
	}, []);

	const pickValueColor = useCallback((type: string) => {
		return unitTypeColors[pluralToSingular[type] ?? type];
	}, []);

	const typeText = useCallback((type: string) => {
		if (type === 'total') {
			return 'learning time';
		}
		if (type === 'videos') {
			return 'videos';
		}
		if (type === 'documents') {
			return 'documents';
		}
		if (type === 'tests') {
			return 'tests';
		}
		if (type === 'webinar') {
			return 'webinars';
		}
		if (type === 'webcontent') {
			return 'web content';
		}
		if (type === 'assignment') {
			return 'assignment';
		}
	}, []);

	useEffect(() => {
		dispatch(getClassroomUnitsAnalytics(Number(currentClassroom?.id)));
	}, [currentClassroom?.id]);

	return (
		<GridLayout pb="5rem" gridTemplateColumns="repeat(6,1fr)">
			{Object.entries(singleClassroomUnitsAnalytics)
				.reverse()
				.map(([key, value]) => {
					return (
						<EnrollmentTypeCard color={pickTypeColor(key)} key={key}>
							<TypeInfo>
								<TypeValue color={pickValueColor(key)}>
									{typeText(key)}
								</TypeValue>
							</TypeInfo>
							<TypeName>{formatDuration(value)}</TypeName>
						</EnrollmentTypeCard>
					);
				})}
		</GridLayout>
	);
};

const StyledPaper = styled(Paper).attrs<PaperProps & { color: string }>({
	elevation: 0,
})`
	padding: 1.25rem;
`;

export const TypeValue = styled.div<{ color: string | undefined }>`
	color: ${(props) => props.color};
	font-size: 1.063rem;
	font-weight: 600;
	text-transform: capitalize;
`;
export const TypeInfo = styled.div`
	display: flex;
	justify-content: space-between;
`;
export const TypeName = styled.div`
	font-size: 1.063rem;
	font-weight: 600;
	grid-row: 2/2;
	color: #251b33;
	text-align: end;
`;

export const EnrollmentTypeCard = styled(StyledPaper)`
	display: flex;
	min-width: 10.5rem;
	flex-direction: column;
	justify-content: space-between;
	gap: 0.5rem;
	margin-bottom: 1rem !important;
	background: ${(props) => (props.color ? rgba(props.color, 0.1) : '')};
	color: ${(props) => props.color};
	max-height: 7.75rem;
	${TypeInfo} {
		color: ${(props) => props.color};
	}
	margin: 0 1rem;
	:first-child {
		margin-left: 0;
	}
	:last-child {
		margin-left: 0;
	}
	span {
		margin-bottom: 1.25rem;
	}
	svg {
		max-height: 100%;
	}
`;

export default ClassroomUnitsAnalytics;
